/**
 * An order-list alert on a manager's For You (core orderalert.ts): "Sanitizer added to the order list. Susan said you're
 * running low." One tap says thanks to the person who spoke up ("Thank Susan": a private kudos, once); one tap opens the order
 * list (where each item is one tap to Amazon, as before). Several adds within ten minutes are one alert, naming who added each.
 * A person who also noticed can be thanked too. Credit for speaking up, never blame (G2b).
 */
import { useState } from "react";
import { ORDER_ALERT_WORDS, firstName, type OrderAlertItemView, type ShiftNoticeView } from "@logwave/core";
import { saveErrorText } from "../lib/failures.ts";
import { useApi, useSession } from "../session.tsx";
import { Icon } from "./Icons.tsx";
import { Button, LinkButton, Pill } from "./ui.tsx";

interface Credit { employeeId: string; name: string; flagId: string; thanked: boolean }

/** Who can be thanked on this alert, once each: the people who flagged, and those who also noticed. */
function creditsOf(items: readonly OrderAlertItemView[]): Credit[] {
  const out = new Map<string, Credit>();
  for (const i of items) {
    for (const p of [i.by, ...i.also]) {
      if (!p.employeeId) continue;
      const have = out.get(p.employeeId);
      const thanked = i.thanked.includes(p.employeeId);
      // The first line this person has that is not yet thanked is the one a tap thanks them for.
      if (!have || (have.thanked && !thanked)) out.set(p.employeeId, { employeeId: p.employeeId, name: p.name, flagId: i.flagId, thanked });
    }
  }
  return [...out.values()];
}

export function OrderAlertBody({ notice, onChanged }: { notice: ShiftNoticeView; onChanged?: () => void | Promise<void> }) {
  const api = useApi();
  const s = useSession();
  const [busy, setBusy] = useState<string>();
  const [err, setErr] = useState<string>();
  const [thanked, setThanked] = useState<Set<string>>(new Set());
  const items = notice.order?.items ?? [];
  const credits = creditsOf(items);
  const main = credits[0];

  async function thank(c: Credit) {
    setBusy(c.employeeId); setErr(undefined);
    try {
      await api.thankOrderAlert(s.org.id, c.flagId, { employeeId: c.employeeId });
      setThanked((t) => new Set(t).add(c.employeeId));
      await onChanged?.();
    } catch (x) { setErr(saveErrorText(x)); } finally { setBusy(undefined); }
  }

  return (
    <div className="stack-sm order-alert" data-testid="order-alert">
      <h2 className="fcard-title">{notice.title}</h2>
      <p className="order-alert-body">{notice.body}</p>
      {credits.length > 0 && (
        <div className="order-alert-thanks stack-sm">
          {credits.map((c, i) => {
            const done = c.thanked || thanked.has(c.employeeId);
            return done
              ? <p key={c.employeeId} className="order-thanked" role="status" data-testid="order-thanked"><Icon name="check" size={18} />{ORDER_ALERT_WORDS.thanked(c.name)}</p>
              : <Button key={c.employeeId} big={i === 0} block variant={i === 0 ? "primary" : "secondary"} icon="spark" busy={busy === c.employeeId} onClick={() => void thank(c)} data-testid="order-thank">{i === 0 ? ORDER_ALERT_WORDS.thank(c.name) : `${ORDER_ALERT_WORDS.thank(c.name)} too`}</Button>;
          })}
        </div>
      )}
      {err && <p className="field-msg" role="alert">{err}</p>}
      <LinkButton block variant={main ? "secondary" : "primary"} icon="cart" to="/order">{ORDER_ALERT_WORDS.open}</LinkButton>
      {main && items.length === 1 && <p className="caption">Only {firstName(main.name)} sees the thank-you.</p>}
      {items.length > 1 && <Pill tone="info" icon="cart">{items.length} items</Pill>}
    </div>
  );
}

/** The line the person who flagged it reads (core orderalert.ts O7), in the toast after "I'm out" / "Running low". */
export const withTold = (words: string, told?: string): string => (told ? `${words} ${told}` : words);
